import { EnrollmentStatus } from "@/domain/enrollment.interface";

/**
 * Frontend display labels for participant status
 */
export const EnrollmentStatusDisplay: Record<EnrollmentStatus, string> = {
  [EnrollmentStatus.ACTIVE]: "Active",
  [EnrollmentStatus.COMPLETED]: "Completed",
  [EnrollmentStatus.DISENROLLED]: "Disenrolled",
  [EnrollmentStatus.PENDING_APPROVAL]: "Pending approval",
  [EnrollmentStatus.DENIED]: "Denied",
};

/**
 * Format backend status to user-friendly display label
 * @param status - Backend status
 * @returns Display label
 */
export const formatEnrollmentStatus = (
  status: string | null | undefined,
): string => {
  if (!status) return "Unknown";

  const upperStatus = status.toUpperCase() as EnrollmentStatus;
  return EnrollmentStatusDisplay[upperStatus] || status;
};

/**
 * Parse frontend display status back to backend format
 * @param displayStatus - Display label (e.g., "Active")
 * @returns Backend status (e.g., "ACTIVE")
 */
export const parseEnrollmentStatus = (displayStatus: string): string => {
  const entry = Object.entries(EnrollmentStatusDisplay).find(
    ([, display]) => display.toLowerCase() === displayStatus.toLowerCase(),
  );
  return entry ? entry[0] : displayStatus.toUpperCase();
};

/**
 * Get all available enrollment statuses for filters/dropdowns
 * @returns Array of display statuses
 */
export const getEnrollmentStatusOptions = (): string[] => {
  return Object.values(EnrollmentStatusDisplay);
};

/**
 * Get status chip color styles for MUI sx prop
 * @param status - Backend status
 * @returns MUI sx style object with bgcolor and borderColor
 */
export const getEnrollmentStatusColor = (
  status: string | null | undefined,
): { bgcolor: string; borderColor: string } => {
  // Chip status colors from spec
  const CHIP_COLORS = {
    successful: { fill: "#E2F4A6", border: "#ACDD49" }, // Green
    failed: { fill: "#FFE7C2", border: "#EAB766" }, // Orange
    missed: { fill: "#FAD1D3", border: "#F9A2A2" }, // Red
    default: { fill: "#E5E6E6", border: "#99979A" }, // Gray
    pendingApproval: { fill: "#FFE7C2", border: "#EAB766" }, // Orange
  };

  if (!status) {
    return {
      bgcolor: CHIP_COLORS.default.fill,
      borderColor: CHIP_COLORS.default.border,
    };
  }

  const upperStatus = status.toUpperCase();

  switch (upperStatus) {
    case EnrollmentStatus.ACTIVE:
      return {
        bgcolor: CHIP_COLORS.successful.fill, // Green
        borderColor: CHIP_COLORS.successful.border,
      };
    case EnrollmentStatus.COMPLETED:
      return {
        bgcolor: CHIP_COLORS.default.fill, // Gray
        borderColor: CHIP_COLORS.default.border,
      };
    case EnrollmentStatus.DISENROLLED:
      return {
        bgcolor: CHIP_COLORS.failed.fill, // Orange
        borderColor: CHIP_COLORS.failed.border,
      };
    case EnrollmentStatus.PENDING_APPROVAL:
      return {
        bgcolor: CHIP_COLORS.pendingApproval.fill, // Orange
        borderColor: CHIP_COLORS.pendingApproval.border,
      };
    case EnrollmentStatus.DENIED:
      return {
        bgcolor: CHIP_COLORS.missed.fill, // Red
        borderColor: CHIP_COLORS.missed.border,
      };
    default:
      return {
        bgcolor: CHIP_COLORS.default.fill, // Gray
        borderColor: CHIP_COLORS.default.border,
      };
  }
};
